import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';
import api from '../../../config/api';

const Wrap = styled.section`
  background: linear-gradient(135deg, #0c1929 0%, #1e3a5f 100%);
  border: 1px solid ${theme.colors.border.primary};
  border-radius: 12px;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.35);
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid ${theme.colors.border.secondary};
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Badge = styled.span`
  font-size: 0.75rem;
  padding: 0.2rem 0.5rem;
  border-radius: 999px;
  background: ${(p) => (p.$on ? 'rgba(16, 185, 129, 0.25)' : 'rgba(100, 116, 139, 0.35)')};
  color: ${(p) => (p.$on ? '#34d399' : theme.colors.text.secondary)};
  border: 1px solid ${(p) => (p.$on ? 'rgba(16,185,129,0.5)' : theme.colors.border.secondary)};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
`;

const Card = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: 10px;
  padding: 1rem;
`;

const PumpName = styled.div`
  font-weight: 600;
  color: ${theme.colors.text.primary};
  margin-bottom: 0.75rem;
  font-size: 1rem;
`;

const StatRow = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.875rem;
  color: ${theme.colors.text.secondary};
  margin-bottom: 0.35rem;
`;

const StatVal = styled.span`
  color: ${theme.colors.text.primary};
  font-weight: 500;
`;

const Timeline = styled.div`
  position: relative;
  height: 36px;
  background: rgba(15, 23, 42, 0.6);
  border-radius: 6px;
  margin-top: 0.75rem;
  overflow: hidden;
`;

const Seg = styled.div`
  position: absolute;
  top: 4px;
  bottom: 4px;
  background: linear-gradient(90deg, #3b82f6, #60a5fa);
  border-radius: 4px;
  opacity: 0.9;
`;

const Ticks = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: ${theme.colors.text.disabled};
  margin-top: 4px;
`;

function formatDur(sec) {
  if (sec == null || Number.isNaN(sec)) return '—';
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m`;
  return `${s}s`;
}

function isRunningLive(live) {
  if (live == null || live.running === undefined) return false;
  const r = live.running;
  if (typeof r === 'boolean') return r;
  if (typeof r === 'number') return r > 0.5;
  if (typeof r === 'string') return r === '1' || r.toLowerCase() === 'on';
  return Boolean(r);
}

const BilgeMonitorSection = ({ dashboardId, config }) => {
  const [stats, setStats] = useState(null);
  const [err, setErr] = useState(null);
  const isDemo = stats?.demo === true || config?.useDemoData === true;

  const load = useCallback(async () => {
    if (!dashboardId) return;
    try {
      const { data } = await api.get(`/api/dashboards/${dashboardId}/bilge-monitor/stats`);
      setStats(data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.error || 'Failed to load bilge data');
    }
  }, [dashboardId]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  if (!config?.enabled || !config.pumps?.length) return null;

  const now = Date.now();
  const dayStart = now - 24 * 3600 * 1000;

  return (
    <Wrap>
      <Head>
        <Title>
          Bilge monitor
          {isDemo && (
            <span
              style={{
                fontSize: '0.7rem',
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 999,
                background: 'rgba(245, 158, 11, 0.2)',
                color: '#fbbf24',
                border: '1px solid rgba(245,158,11,0.45)',
              }}
            >
              Sample data
            </span>
          )}
          <span style={{ fontSize: '0.8rem', fontWeight: 400, color: theme.colors.text.secondary }}>
            Last 24h activity & season totals
          </span>
        </Title>
      </Head>
      {err && (
        <div style={{ color: theme.colors.warning.main, marginBottom: 8, fontSize: 14 }}>{err}</div>
      )}
      <Grid>
        {(stats?.pumps || []).map((p) => {
          const running = isRunningLive(p.live);
          let extraSec = 0;
          if (p.openRun?.started_at) {
            extraSec = Math.floor((now - new Date(p.openRun.started_at).getTime()) / 1000);
          }
          const total24 = p.stats24h.totalSeconds + (p.openRun ? extraSec : 0);

          return (
            <Card key={p.pumpId}>
              <PumpName>
                {p.name}
                <Badge $on={running} style={{ marginLeft: 8 }}>
                  {running ? 'Running' : 'Off'}
                </Badge>
              </PumpName>
              <StatRow>
                <span>Voltage</span>
                <StatVal>
                  {p.live?.voltage != null && p.live.voltage !== ''
                    ? `${Number(p.live.voltage).toFixed(1)} V`
                    : '—'}
                </StatVal>
              </StatRow>
              <StatRow>
                <span>Current</span>
                <StatVal>
                  {p.live?.current != null && p.live.current !== ''
                    ? `${Number(p.live.current).toFixed(2)} A`
                    : '—'}
                </StatVal>
              </StatRow>
              <StatRow>
                <span>Runs (24h)</span>
                <StatVal>
                  {p.stats24h.runCount + (p.openRun ? 1 : 0)} / {formatDur(total24)}
                </StatVal>
              </StatRow>
              <StatRow>
                <span>Season runs / time</span>
                <StatVal>
                  {p.season.runCount} / {formatDur(p.season.totalSeconds)}
                </StatVal>
              </StatRow>
              <div style={{ fontSize: 11, color: theme.colors.text.disabled, marginTop: 8 }}>
                Season starts {config.seasonStartMonth || 4}/{config.seasonStartDay || 1} (rolling)
              </div>
              <Timeline>
                {(p.runs24h || []).map((run) => {
                  const start = new Date(run.started_at).getTime();
                  const end = run.ended_at
                    ? new Date(run.ended_at).getTime()
                    : now;
                  const left = Math.max(0, ((start - dayStart) / (24 * 3600 * 1000)) * 100);
                  const w = Math.min(100 - left, ((end - start) / (24 * 3600 * 1000)) * 100);
                  if (w <= 0) return null;
                  return <Seg key={run.id} style={{ left: `${left}%`, width: `${w}%` }} title={`${run.started_at}`} />;
                })}
                {p.openRun && (
                  <Seg
                    key="open"
                    style={{
                      left: `${Math.max(0, ((new Date(p.openRun.started_at).getTime() - dayStart) / (24 * 3600 * 1000)) * 100)}%`,
                      width: `${Math.min(100, ((now - new Date(p.openRun.started_at).getTime()) / (24 * 3600 * 1000)) * 100)}%`,
                      background: 'linear-gradient(90deg,#f59e0b,#fbbf24)',
                    }}
                    title="Current run"
                  />
                )}
              </Timeline>
              <Ticks>
                <span>24h ago</span>
                <span>Now</span>
              </Ticks>
            </Card>
          );
        })}
      </Grid>
    </Wrap>
  );
};

export default BilgeMonitorSection;
